import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { caseAPI } from '../../services/api';
import Loading from '../common/Loading';
import toast from 'react-hot-toast';

const CaseDashboard = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStatusTemplate, setShowStatusTemplate] = useState(false);
  const [showHardshipModal, setShowHardshipModal] = useState(false);
  const [hardshipStatus, setHardshipStatus] = useState('idle'); // idle, loading, success
  const [statusUpdateStatus, setStatusUpdateStatus] = useState('idle'); // idle, loading, success
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [clientInfo, setClientInfo] = useState(null);

  const imageFiles = ['/files/pdf2.png', '/files/pdf1.png'];
  const [isSliding, setIsSliding] = useState(false);

  useEffect(() => {
    // Check if case number exists in localStorage
    const caseNumber = localStorage.getItem('publicCaseNumber');
    
    if (!caseNumber) {
      toast.error('No case number found. Please lookup your case first.');
      navigate('/case-lookup');
      return;
    }

    fetchCases(caseNumber);
  }, [navigate]);

  const handleImageClick = () => {
    if (!isSliding) {
      setIsSliding(true);
      setCurrentSlide((prev) => (prev + 1) % imageFiles.length);
      setTimeout(() => setIsSliding(false), 300); // Match animation duration
    }
  };

  const fetchCases = async (caseNumber) => {
    try {
      const response = await caseAPI.getMyCase(caseNumber);
      
      if (response.data && response.data.length > 0) {
        setCases(response.data);
        // Set client info from the first case
        setClientInfo({
          name: response.data[0].clientName,
          location: response.data[0].clientLocation || 'New York, NY',
          age: response.data[0].clientAge || '32',
          dob: response.data[0].clientDOB || '01/15/1993',
          status: response.data[0].clientStatus || 'ACTIVE'
        });
      } else {
        toast.error('No cases found for this case number.');
        navigate('/case-lookup');
      }
    } catch (error) {
      toast.error('Failed to load cases');
      navigate('/case-lookup');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatusTemplate = () => {
    setShowStatusTemplate(!showStatusTemplate);
    // Reset status when closing
    if (showStatusTemplate) {
      setStatusUpdateStatus('idle');
    }
  };

  const toggleSlideshow = () => {
    setShowSlideshow(!showSlideshow);
    setCurrentSlide(0);
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    
    // Show loader
    setStatusUpdateStatus('loading');
    
    // Wait 4 seconds
    setTimeout(() => {
      // Show success message
      setStatusUpdateStatus('success');
      
      // Wait 2 more seconds then close
      setTimeout(() => {
        setStatusUpdateStatus('idle');
        setShowStatusTemplate(false);
      }, 2000);
    }, 4000);
  };

  const handleHardshipSubmit = async (e) => {
    e.preventDefault();
    
    // Show loader
    setHardshipStatus('loading');
    
    // Wait 3 seconds
    setTimeout(() => {
      // Show success message
      setHardshipStatus('success');
      
      // Wait 2 more seconds then close modal
      setTimeout(() => {
        setHardshipStatus('idle');
        setShowHardshipModal(false);
      }, 2000);
    }, 3000);
  };

  const handleCreateProfile = () => {
    navigate('/signup');
  };

  const totalFines = cases.reduce((sum, c) => sum + c.balance, 0);

  if (loading) {
    return <Loading message="Loading your Case dashboard..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-light text-gray-400 tracking-wider mb-6">
            ACCOUNT SUMMARY
          </h1>
          
          {/* Notice Box */}
          <div className="bg-white border-t-4 border-red-600 shadow-sm rounded-lg p-6 mb-6">
            <p className="text-red-600 font-semibold text-sm leading-snug mb-2">
              NOTICE: THIS ACCOUNT HAS PENDING CITATIONS. PLEASE CONTACT YOUR LOCAL U.S. FEDERAL DISTRICT COURTS CIVIL SERVICES DIVISION IN ORDER TO COMPLETE RESCHEDULING AT
            </p>
            <p className="text-gray-900 text-center font-semibold leading-snug mt-2">
              (202) 534-6578
            </p>
          </div>

          {/* Surety Bond Info */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-3">Surety Bond</h2>
            <p className="text-gray-700 text-sm mb-1 leading-snug">
              <span className="font-semibold">Required:</span> A surety bond is a financial guarantee ensuring that you meet specific obligations, such as:
            </p>
            <ul className="text-gray-700 text-sm ml-4 list-disc mb-1 leading-snug">
              <li style={{ marginBottom: '0.125rem' }}>Paying required fees</li>
              <li style={{ marginBottom: '0.125rem' }}>Attending court as scheduled</li>
              <li style={{ marginBottom: '0.125rem' }}>Complying with court-ordered conditions</li>
            </ul>
            <p className="text-gray-700 text-sm mb-1 leading-snug">
              If these obligations are not met, the bond may be forfeited, and you could be held responsible for any losses or additional costs.
            </p>
            <p className="text-gray-700 text-sm mb-1 leading-snug">
              Surety bonds are recognized as a permitted government-backed financial instrument. The bond is typically filed to confirm the bond as recognized and guaranteed collateral to the relevant parties or authorities.
            </p>
            <p className="text-gray-700 text-sm leading-snug">
              Upon successful case completion and fulfillment of all obligations, the surety bond is typically refunded.
            </p>
          </div>

          {/* User Info Card */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-start space-x-4">
              <div className="w-24 h-24 bg-red-700 rounded-md flex-shrink-0"></div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900" style={{ marginBottom: '0.25rem', lineHeight: '1.3' }}>
                  {clientInfo?.name || 'KENNETH SMITH'}
                </h3>
                <p className="text-gray-600 text-sm leading-snug" style={{ marginBottom: '0.125rem' }}>
                  {clientInfo?.location || 'New York, NY'}
                </p>
                <p className="text-gray-600 text-sm leading-snug" style={{ marginBottom: '0.125rem' }}>
                  Age: {clientInfo?.age || '32'}
                </p>
                <p className="text-gray-600 text-sm leading-snug" style={{ marginBottom: '0.125rem' }}>
                  DOB: {clientInfo?.dob || '01/15/1993'}
                </p>
                <p className="text-red-600 text-sm font-semibold leading-snug" style={{ marginBottom: '0.25rem' }}>
                  Status: {clientInfo?.status || 'ACTIVE'}
                </p>
                
                <button 
                  onClick={toggleSlideshow}
                  className="text-blue-600 text-sm flex items-center hover:underline focus:outline-none"
                  style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
                >
                  <span className="w-4 h-4 bg-blue-600 rounded-full mr-2 flex items-center justify-center text-white text-xs">i</span>
                  <span className="underline ml-1">{showSlideshow ? 'Close Document' : 'View Documents'}</span>
                </button>

                <button 
                  onClick={toggleStatusTemplate}
                  className="text-blue-600 text-sm flex items-center hover:underline focus:outline-none"
                  style={{ marginTop: '0.5rem' }}
                >
                  <span className="w-4 h-4 bg-blue-600 rounded-full mr-2 flex items-center justify-center text-white text-xs">i</span>
                  <span className="underline ml-1">Submit Documents</span>
                </button>
              </div>
              <button className="text-gray-400 hover:text-gray-600">
                <span className="text-xl">⊗</span>
              </button>
            </div>
          </div>
        </div>

        {/* Image Slideshow */}
        {showSlideshow && (
          <div className="bg-white border-2  rounded-lg shadow-lg p-4 my-4">
            <div className="relative overflow-hidden">
              <img 
                src={imageFiles[currentSlide]}
                alt={`Slide ${currentSlide + 1}`}
                onClick={handleImageClick}
                className={`w-full h-96 object-contain border border-gray-300 rounded bg-gray-50 cursor-pointer transition-transform duration-300 ${
                  isSliding ? 'animate-slide-in' : ''
                }`}
              />
              
              {/* Slide Counter */}
              <div className="text-center mt-3">
                <span className="text-sm text-gray-600">
                  {currentSlide + 1} / {imageFiles.length}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Status Update Template Dropdown */}
        {showStatusTemplate && (
          <div className="bg-white border-2 border-red-500 rounded-lg shadow-lg p-6 mb-6">
            {/* Loading State */}
            {statusUpdateStatus === 'loading' && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
                <p className="text-gray-700 font-semibold">Processing your submission...</p>
              </div>
            )}

            {/* Success State */}
            {statusUpdateStatus === 'success' && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="bg-green-100 rounded-full p-4 mb-4">
                  <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-xl font-bold text-green-600">Successfully submitted</p>
              </div>
            )}

            {/* Form State */}
            {statusUpdateStatus === 'idle' && (
              <>
                <div className="text-center mb-3">
                  <h3 className="text-xl font-bold text-gray-900 leading-snug" style={{ marginBottom: '0.25rem' }}>
                    United States District Court
                  </h3>
                  <p className="text-sm text-gray-600 leading-snug" style={{ marginBottom: '0.125rem' }}>
                    Southern District of New York
                  </p>
                  <p className="text-xs text-gray-500 leading-snug">Civil Services Division</p>
                </div>

                <form onSubmit={handleStatusSubmit}>
                  <div className="border-t border-b border-gray-300 py-3 mb-3">
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Case Number:</label>
                        <input 
                          type="text" 
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          placeholder="Enter case number"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Date:</label>
                        <input 
                          type="date" 
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Defendant Name:</label>
                      <input 
                        type="text" 
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        defaultValue={clientInfo?.name}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Status Update Type:</label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm" required>
                        <option value="">Select update type</option>
                        <option value="appearance">Court Appearance Confirmation</option>
                        <option value="payment">Payment Submission</option>
                        <option value="compliance">Compliance Update</option>
                        <option value="request">Rescheduling Request</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Details:</label>
                      <textarea 
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        rows="4"
                        placeholder="Provide details about your status update..."
                        required
                      ></textarea>
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Supporting Documents:</label>
                      <input 
                        type="file" 
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        multiple
                      />
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
                    <p className="text-xs text-gray-700 leading-snug" style={{ marginBottom: '0.25rem' }}>
                      <span className="font-semibold">Important Notice:</span> All status updates must be accurate and truthful. False statements may result in additional penalties or criminal charges.
                    </p>
                    <p className="text-xs text-gray-600 leading-snug">
                      By submitting this form, you acknowledge that the information provided is true and correct to the best of your knowledge.
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <button 
                      type="button"
                      onClick={toggleStatusTemplate}
                      className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded text-sm transition-colors"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit"
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded text-sm transition-colors"
                    >
                      Submit Status Update
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        )}

        {/* Cases Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-blue-900 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold">Statute</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Charge</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Fine</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((caseItem, index) => (
                <tr 
                  key={caseItem._id}
                  className={`${index % 2 === 0 ? 'bg-yellow-50' : 'bg-white'} hover:bg-gray-100 transition-colors`}
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{caseItem.statute || '18 U.S.C. §'}</div>
                    <div className="text-xs text-gray-600">{caseItem.statuteCode || caseItem.caseNumber}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {caseItem.charge || caseItem.description}
                    </div>
                  </td>
                  <td className="pr-6 py-4 text-left">
                    <div className="text-sm font-semibold text-gray-900">
                      ${caseItem.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Create Profile Button */}
          <div className="bg-white px-6 py-4 flex justify-center border-t border-gray-200">
            <button 
              onClick={handleCreateProfile}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-md text-sm transition-colors"
            >
              CREATE PROFILE
            </button>
          </div>

          {/* Financial Hardship Button */}
          <div className="bg-blue-900 px-6 py-4 flex justify-center">
            <button 
              onClick={() => setShowHardshipModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-md text-sm transition-colors"
            >
              FILE FOR FINANCIAL HARDSHIP
            </button>
          </div>

          {/* Post Surety Bond */}
          <div className="bg-blue-100 px-6 py-3">
            <p className="text-sm text-gray-700 leading-snug">Post Surety Bond</p>
          </div>

          {/* Total Fine */}
          <div className="bg-white px-6 py-4 flex justify-between items-center border-t">
            <span className="text-lg font-semibold text-gray-900">Total Fine</span>
            <span className="text-2xl font-bold text-gray-900">
              ${totalFines.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Financial Hardship Modal */}
      {showHardshipModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-lg">
              <h2 className="text-lg font-bold text-gray-900">File for Financial Hardship</h2>
              <button 
                onClick={() => setShowHardshipModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                disabled={hardshipStatus !== 'idle'}
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {hardshipStatus === 'loading' && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
                  <p className="text-gray-700 font-semibold">Processing your request...</p>
                </div>
              )}

              {hardshipStatus === 'success' && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="bg-green-100 rounded-full p-4 mb-4">
                    <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-xl font-bold text-green-600">Successfully filed for financial hardship</p>
                </div>
              )}

              {hardshipStatus === 'idle' && (
                <form onSubmit={handleHardshipSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Monthly Income:</label>
                      <input 
                        type="text" 
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter monthly income"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Monthly Expenses:</label>
                      <input 
                        type="text" 
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter monthly expenses"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Explanation:</label>
                      <textarea 
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="4"
                        placeholder="Explain your situation..."
                        required
                      ></textarea>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="mt-6">
                    <button 
                      type="submit"
                      className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded text-sm transition-colors"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseDashboard;